import { useRef, useCallback, useMemo } from "react";
import _ from "lodash";
import useReduxState from "Hooks/useReduxState";
import { useI18n } from "Hooks";
import { getDefaultStudentNameOrderPreference } from "modules/CommonSelectors";
import { TAB_TYPES } from "../context/ParticipantsModalContext";
import { normaliseParticipant } from "../utils/normaliseParticipant";

// NOTE: The existing participant queries (getParticipantStudentsQuery,
// getParticipantStaffQuery, getParticipantFamilyMembersQuery) use
// OrgConnectionFilterInput and FamilyMembersFilterInput, neither of which
// supports a `userIds` filter field (confirmed by codebase search — no
// existing query passes userIds into these filter types). This means targeted
// per-ID fetching via those queries is not possible.
//
// Approach taken — client-side normalisation from associatedEntities:
//   For custom participant lists, associatedEntities already contains the full
//   set of { id, __typename, ...fields } for every hand-picked participant.
//   This hook normalises those entities client-side using normaliseParticipant
//   and builds detailsMap immediately, without any network request.
//   onRangeChanged is still exposed (and wired to Virtuoso in Task 7) but
//   has no fetching side-effect — Virtuoso uses it purely for scroll tracking.
//
// Known limitation: If the backend adds userIds filtering to
// OrgConnectionFilterInput in the future, this hook should be updated to lazy-
// fetch user details via getParticipantStudentsQuery/getParticipantStaffQuery/
// getParticipantFamilyMembersQuery scoped to the visible IDs rather than
// normalising client-side.

/**
 * Manages virtualised display of a custom participant list where all user IDs
 * are already known from associatedEntities.
 *
 * Because the participant queries do not support filtering by userIds, all
 * participant details are normalised client-side directly from associatedEntities.
 * The hook still exposes onRangeChanged for Virtuoso scroll integration and
 * applies client-side search filtering so the visible list reacts to searchText.
 *
 * @param {{
 *   associatedEntities: Array<{ id: string, __typename: string }>,
 *   activeTab: string,
 *   searchText: string,
 *   organizationId: string,
 * }} params
 * @param {string} params.organizationId - Reserved for future use when the backend
 *   supports userIds filtering on participant queries; not consumed by this hook today.
 * @returns {{
 *   onRangeChanged: ({ startIndex: number, endIndex: number }) => void,
 *   detailsMap: Record<string, object>,
 *   totalCount: { students: number, staff: number, family: number },
 *   isLoading: boolean,
 * }}
 */
const useCustomParticipantsBatch = ({
  associatedEntities,
  activeTab,
  searchText,
  organizationId, // reserved for future use when backend supports userIds filtering
}) => {
  const { t } = useI18n(["common"]);
  const defaultStudentNameOrderPreference = useReduxState(
    getDefaultStudentNameOrderPreference
  );

  // Group entity IDs by __typename once per associatedEntities reference.
  const grouped = useMemo(() => _.groupBy(associatedEntities, "__typename"), [
    associatedEntities,
  ]);

  const studentIds = useMemo(() => _.map(grouped["Student"] || [], "id"), [
    grouped,
  ]);
  const staffIds = useMemo(() => _.map(grouped["Staff"] || [], "id"), [
    grouped,
  ]);
  const familyIds = useMemo(() => _.map(grouped["FamilyMember"] || [], "id"), [
    grouped,
  ]);

  // Build detailsMap eagerly from associatedEntities — no network requests
  // needed because all participant data is already present in the event payload.
  const detailsMap = useMemo(() => {
    const map = {};
    _.forEach(associatedEntities, participant => {
      if (_.get(participant, "id")) {
        map[participant.id] = normaliseParticipant(
          participant,
          defaultStudentNameOrderPreference,
          t
        );
      }
    });
    return map;
  }, [associatedEntities, defaultStudentNameOrderPreference, t]);

  // detailsMapRef mirrors detailsMap so onRangeChanged can read the current
  // value without needing detailsMap in its dependency array — consistent with
  // the pattern in useGradeBatchDetails and useCourseBatchDetails.
  const detailsMapRef = useRef({});
  detailsMapRef.current = detailsMap;

  // inFlightIds is kept as a ref for API-parity with the other batch hooks.
  // It is not used in the client-side approach but is preserved so this hook
  // can be upgraded to network-fetching when userIds filtering is supported.
  const inFlightIds = useRef(new Set());

  // Derive the active tab's ID list for Virtuoso range tracking.
  const activeTabIds = useMemo(() => {
    if (activeTab === TAB_TYPES.STUDENTS) return studentIds;
    if (activeTab === TAB_TYPES.STAFF) return staffIds;
    if (activeTab === TAB_TYPES.FAMILY) return familyIds;
    return [];
  }, [activeTab, studentIds, staffIds, familyIds]);

  // Apply client-side search filter to the IDs visible in the active tab.
  // Returns the subset of activeTabIds whose normalised name matches searchText.
  const filteredActiveTabIds = useMemo(() => {
    const trimmed = _.trim(searchText);
    if (_.isEmpty(trimmed)) return activeTabIds;
    const lowerSearch = _.toLower(trimmed);
    return _.filter(activeTabIds, id => {
      const entry = detailsMap[id];
      return entry && _.includes(_.toLower(entry.name), lowerSearch);
    });
  }, [activeTabIds, searchText, detailsMap]);

  // onRangeChanged is wired to Virtuoso's rangeChanged prop (Task 7).
  // In the current client-side approach it has no async side-effect — all
  // entries are already in detailsMap. The implementation still inspects
  // inFlightIds and detailsMapRef to maintain the same interface contract as
  // useGradeBatchDetails, making a future network-fetch upgrade straightforward.
  const onRangeChanged = useCallback(
    ({ startIndex, endIndex }) => {
      const visibleIds = filteredActiveTabIds.slice(startIndex, endIndex + 1);
      // In client-side mode all IDs are already in detailsMap, so toFetch is
      // always empty. This block is preserved as a structural scaffold for when
      // backend userIds filtering is added and the hook switches to network fetching.
      const toFetch = _.filter(
        visibleIds,
        id => !detailsMapRef.current[id] && !inFlightIds.current.has(id)
      );
      // No-op in client-side mode — all IDs are already in detailsMap.
      if (_.isEmpty(toFetch)) return;
      // Guard: if somehow an ID is missing (e.g. partial payload), mark it
      // in-flight so we don't log duplicates, and log for debugging.
      _.forEach(toFetch, id => inFlightIds.current.add(id));
    },
    [filteredActiveTabIds]
  );

  return {
    onRangeChanged,
    detailsMap,
    filteredActiveTabIds,
    totalCount: {
      students: studentIds.length,
      staff: staffIds.length,
      family: familyIds.length,
    },
    // Always false — all data is synchronously derived from associatedEntities.
    isLoading: false,
  };
};

export default useCustomParticipantsBatch;
